/** Reading and writing a JSON value by path, as the options form and the backend's errors name it. */
import { ApiError } from '@/api/client';
import type { JsonSchema } from './SchemaForm';

export type Path = (string | number)[];

export function getAt(value: unknown, path: Path): unknown {
  let at: unknown = value;
  for (const key of path) {
    if (at === null || typeof at !== 'object') return undefined;
    at = (at as Record<string | number, unknown>)[key];
  }
  return at;
}

export function setAt(value: unknown, path: Path, next: unknown): unknown {
  if (!path.length) return next;
  const [key, ...rest] = path;
  if (typeof key === 'number') {
    const list = Array.isArray(value) ? [...value] : [];
    list[key] = setAt(list[key], rest, next);
    return list;
  }
  const obj = value !== null && typeof value === 'object' && !Array.isArray(value) ? { ...(value as Record<string, unknown>) } : {};
  obj[key!] = setAt(obj[key!], rest, next);
  return obj;
}

export function defaultsOf(schema: JsonSchema): unknown {
  if (schema.type !== 'object') return schema.default;
  const out: Record<string, unknown> = {};
  for (const [key, sub] of Object.entries(schema.properties ?? {})) {
    const d = defaultsOf(sub);
    if (d !== undefined) out[key] = d;
  }
  return out;
}

export const pathKey = (path: Path) => path.join('.');

/** The backend reports one `"<path>: <text>"` line per problem (`report.invalid_options`). */
export function optionErrors(err: unknown): Record<string, string> {
  if (!(err instanceof ApiError) || err.code !== 'report.invalid_options') return {};
  const out: Record<string, string> = {};
  for (const line of err.message.split('\n')) {
    const at = line.indexOf(': ');
    if (at > 0) out[line.slice(0, at)] = line.slice(at + 2);
  }
  return out;
}
